'use client';

import { useMutation as useTanstackMutation } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useMemo, useRef } from 'react';
const DEFAULT_DEBOUNCE_MS = 300;
const wrapMutate = (mutate, cancelDebounce) => {
  return (args, options) => {
    cancelDebounce?.();
    mutate(args, options);
  };
};
const wrapStatus = status => {
  switch (status) {
    case 'idle':
      return 'IDLE';
    case 'pending':
      return 'PENDING';
    case 'error':
      return 'ERROR';
    case 'success':
      return 'SUCCESS';
    default:
      return 'IDLE';
  }
};
const wrapMutateDebounce = (mutate, onDebounceMutate, debounceMs) => {
  let debounceCallback;
  const wrappedMutate = args => {
    mutate(args, {
      onSettled(data, error) {
        debounceCallback?.(data, error);
      }
    });
  };
  const debouncedMutate = debounce(wrappedMutate, debounceMs ?? DEFAULT_DEBOUNCE_MS);
  const debouncedMutateFn = (args, opts) => {
    debounceCallback = opts?.onSettled;
    debouncedMutate.cancel();
    debouncedMutate(args);
    return onDebounceMutate?.(args) ?? undefined;
  };
  const cancelMutationDebounce = () => {
    debouncedMutate.cancel();
  };
  return [debouncedMutateFn, cancelMutationDebounce];
};
/**
 * A wrapper around tanstack query useMutation to avoid leaking framework specific code.
 * Should always be used from every publicly exposed mutation.
 *
 * @param options tanstack mutation options
 * @param queryClient tanstack query client
 * @returns mutation api
 */
export function useMutation(options, queryClient) {
  const {
    custom: customOptions,
    ...tanstackOptions
  } = options;
  const mutationResult = useTanstackMutation(tanstackOptions, queryClient);
  // Use immutable reference to only wrap callback in debounce once.
  // If we pass the customOptions.onDebounce directly to wrapMutateDebounce it will cause the
  // useMemo to be updated several times since the reference cannot be ensured to be stable.
  const onDebounceRef = useRef(args => {
    return customOptions?.onDebounce?.(args);
  });
  const [mutateDebounce, cancelDebounce] = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    return wrapMutateDebounce(mutationResult.mutate, onDebounceRef.current, customOptions?.debounceMs);
  }, [customOptions?.debounceMs, mutationResult.mutate]);
  return useMemo(() => {
    return {
      mutateDebounce,
      mutate: wrapMutate(mutationResult.mutate, cancelDebounce),
      status: wrapStatus(mutationResult.status)
    };
  }, [cancelDebounce, mutateDebounce, mutationResult.mutate, mutationResult.status]);
}